package com.advaita.cloud.drive.ui.api

import com.advaita.cloud.drive.shared.AuthConfigDto
import com.advaita.cloud.drive.shared.CompressRequest
import com.advaita.cloud.drive.shared.CompressResponse
import com.advaita.cloud.drive.shared.CreateShareRequest
import com.advaita.cloud.drive.shared.FileAccessResponse
import com.advaita.cloud.drive.shared.GivenSharesResponse
import com.advaita.cloud.drive.shared.MeAccessDto
import com.advaita.cloud.drive.shared.PickerResponse
import com.advaita.cloud.drive.shared.SetShareLevelRequest
import com.advaita.cloud.drive.shared.ShareDto
import com.advaita.cloud.drive.shared.SharedPageResponse
import com.advaita.cloud.drive.shared.SourceStatus
import com.advaita.cloud.drive.shared.CuratedCatalogResponse
import com.advaita.cloud.drive.shared.CuratedWorkflowDto
import com.advaita.cloud.drive.shared.CuratedRunRequest
import com.advaita.cloud.drive.shared.ExtractDestinationDto
import com.advaita.cloud.drive.shared.ExtractRequest
import com.advaita.cloud.drive.shared.ExtractResponse
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileTreeResponse
import com.advaita.cloud.drive.shared.MeAdmissionDto
import com.advaita.cloud.drive.shared.PreviewDescriptorDto
import com.advaita.cloud.drive.shared.PullResponse
import com.advaita.cloud.drive.shared.ResumeKeyResponse
import com.advaita.cloud.drive.shared.S3Credential
import com.advaita.cloud.drive.shared.UploadConfirmPartRequest
import com.advaita.cloud.drive.shared.UploadPartUrlResponse
import com.advaita.cloud.drive.shared.UploadStartMultipartResponse
import com.advaita.cloud.drive.shared.UploadStartRequest
import com.advaita.cloud.drive.shared.UploadStatusResponse
import com.advaita.cloud.drive.shared.UserInfoDto
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response
import org.w3c.files.File
import org.w3c.xhr.FormData

/** OK: deleted (a file). ACCEPTED: a folder, now deleting in the background (`202`). */
enum class DeleteResult { OK, ACCEPTED, NOT_FOUND }

/**
 * A preview byte response, still undecoded.
 *
 * The raw [Response] is handed back rather than a String or a Blob so the shell
 * decodes it once, the way the chosen renderer needs it — text for TEXT, a blob
 * URL for the binary renderers later subtasks add. A body cannot be read twice,
 * so that decision belongs to the caller.
 *
 * [truncated] comes from the response header, which is authoritative: the server
 * derives it from the bytes it actually read, so it is right even when the file's
 * size column is null.
 */
class PreviewFetch(
    val response: Response,
    val truncated: Boolean,
    /**
     * `<w>x<h> from <w>x<h>` when the body is a downscaled rendition rather
     * than the file's own bytes (ADV-1207). Null when it is the original.
     */
    val rendition: String? = null,
    /**
     * The type of the BYTES, when the server says it differs from the file's.
     * A TIFF preview arrives as PNG. Null means "the descriptor's type".
     */
    val payloadContentType: String? = null,
)

class FolderNotFoundException(val path: String) : RuntimeException("folder not found: $path")

/**
 * The server will not serve preview bytes for this file, and said why.
 *
 * `/preview/content` answers 409 with a [PreviewDescriptorDto] as the BODY, so
 * the reason survives two cases the descriptor call cannot cover: a client that
 * skipped it, and a descriptor that was optimistic because the file's size
 * column was null. The second is real for a REFUSE format — the descriptor says
 * RENDER, the read discovers the object is over the ceiling, and only the 409
 * knows that.
 *
 * Distinct from [ApiException] on purpose: this is a normal answer, so the pane
 * states the reason instead of showing a red failure with a Retry button that
 * will get the same answer.
 */
class PreviewNotRenderableException(val descriptor: PreviewDescriptorDto) :
    RuntimeException(descriptor.message ?: "This file cannot be previewed.")

private val apiJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = true
}


/** How bad a source status is, for [mergeSharedPage]: any status that is not `ok` beats `ok`. */
private fun statusRank(s: String?): Int = when (s) {
    null -> 0
    SourceStatus.OK -> 1
    SourceStatus.REJECTED -> 2
    SourceStatus.REFUSED -> 3
    else -> 4 // unavailable, or a status this build does not know
}

private fun worst(a: String?, b: String?): String? = if (statusRank(b) > statusRank(a)) b else a

/**
 * One more page of `GET /api/shared` onto [acc]. The rows and the shares add up, `truncated`
 * holds if any page said so, and each status is the worst any page gave, so one bad page is
 * never hidden by a later good one.
 */
internal fun mergeSharedPage(acc: SharedPageResponse?, page: SharedPageResponse): SharedPageResponse =
    acc?.copy(
        items = acc.items + page.items,
        shares = acc.shares + page.shares,
        truncated = acc.truncated || page.truncated,
        nextPageToken = page.nextPageToken,
        sharesStatus = worst(acc.sharesStatus, page.sharesStatus),
        peopleStatus = worst(acc.peopleStatus, page.peopleStatus),
    ) ?: page

object FilesApi {

    suspend fun listFiles(path: String = "/"): List<FileDto> {
        val response = authedFetch("/api/files?path=${encodeURIComponent(path)}")
        if (response.status.toInt() == 404) throw FolderNotFoundException(path)
        ensureOk(response, "list files")
        return apiJson.decodeFromString(response.text().await())
    }

    /** Recursive name search under [path] (ADV-1215): newest first, capped at [limit]. */
    suspend fun searchFiles(path: String, namePattern: String, limit: Int = 50): FileTreeResponse {
        val query = "recursive=true&sort=modified&limit=$limit" +
            "&path=${encodeURIComponent(path)}&name=${encodeURIComponent(namePattern)}"
        val response = authedFetch("/api/files?$query")
        ensureOk(response, "search files")
        return apiJson.decodeFromString(response.text().await())
    }

    /** ADV-1330: [target] is My Drive's folder (`parentPath`) or a shared folder (`parentId`). */
    /**
     * ADV-1330 (Tim, 2026-09-28): the recursive name search inside a folder shared with the
     * caller: `GET /api/files?folderId=&recursive=true`, with [searchFiles]' sort and limit. The
     * route checks `read` on the folder once, and lists only rows the sharee can open.
     */
    suspend fun searchFolder(folderId: String, namePattern: String, limit: Int = 50, pageToken: String? = null): FileTreeResponse {
        val query = "recursive=true&sort=modified&limit=$limit" +
            "&folderId=${encodeURIComponent(folderId)}&name=${encodeURIComponent(namePattern)}" +
            (pageToken?.let { "&pageToken=${encodeURIComponent(it)}" } ?: "")
        val response = authedFetch("/api/files?$query")
        ensureOk(response, "search files")
        return apiJson.decodeFromString(response.text().await())
    }

    suspend fun createFolder(target: CreateTarget, name: String): FileDto {
        val payload = encodeCreateBody(createFolderBody(target, name))
        val response = authedFetch(
            "/api/folders",
            method = "POST",
            jsonBody = payload,
        )
        capabilityDenied(response, "create folder")?.let { throw it }
        destinationGone(response, "create folder")?.let { throw it }
        ensureOk(response, "create folder")
        return apiJson.decodeFromString(response.text().await())
    }

    suspend fun uploadFile(parentPath: String, file: File, relativePath: String? = null): FileDto {
        val formData = FormData()
        formData.append("file", file)
        formData.append("parentPath", parentPath)
        if (!relativePath.isNullOrBlank()) {
            formData.append("relativePath", relativePath)
        }
        val response = authedFetch("/api/files", method = "POST", formBody = formData)
        capabilityDenied(response, "upload file")?.let { throw it }
        ensureOk(response, "upload file")
        return apiJson.decodeFromString(response.text().await())
    }

    /** ADV-1330: [target] as in [createFolder]. */
    suspend fun pull(
        target: CreateTarget,
        urls: List<String>,
        s3Credentials: List<S3Credential> = emptyList(),
    ): PullResponse {
        val payload = encodeCreateBody(pullBody(target, urls, s3Credentials))
        val response = authedFetch("/api/pull", method = "POST", jsonBody = payload)
        if (!response.ok) {
            val fields = runCatching { bodyFields(response.text().await()) }.getOrNull().orEmpty()
            throw pullFailure(response.status.toInt(), response.statusText, fields)
        }
        return apiJson.decodeFromString(response.text().await())
    }

    /**
     * Enqueue a whole-archive extract. The backend creates the destination folder row
     * immediately, so the caller only needs to refresh: progress arrives folded onto
     * FileDto.archiveProgress. A non-OK response carries the backend's reason
     * (unsupported extension, not UPLOADED, not yours) — surfaced as a toast.
     */
    suspend fun extract(fileId: String, to: CreateTarget? = null): ExtractResponse {
        // ADV-1529: [to] null = beside the archive. A picked folder goes as parentPath or parentId.
        val payload = encodeCreateBody(
            when (to) {
                null -> ExtractRequest(fileId)
                is CreateTarget.MyDrive -> ExtractRequest(fileId, parentPath = to.path)
                is CreateTarget.SharedFolder -> ExtractRequest(fileId, parentId = to.folderId)
            },
        )
        val response = authedFetch("/api/archive/extract", method = "POST", jsonBody = payload)
        if (!response.ok) {
            capabilityDenied(response, "extract archive")?.let { throw it }
            val reason = runCatching {
                apiJson.decodeFromString<Map<String, String>>(response.text().await())["error"]
            }.getOrNull()
            // Same idiom as [pull]: the backend's own message (unsupported extension, not
            // UPLOADED, not yours) is already human, so prefer it; fall back to the
            // mapped status rather than a bare code.
            throw reason?.let { RuntimeException(it) }
                ?: ApiException(response.status.toInt(), "extract archive", response.statusText)
        }
        return apiJson.decodeFromString(response.text().await())
    }

    /**
     * ADV-1529: may the caller extract [fileId] into [to] (null = beside the archive)? The
     * server runs the extract's own checks and creates nothing. A refusal of the archive itself
     * (no `download`, gone, an outage) throws, as [extract] would.
     */
    suspend fun extractDestinationAllowed(fileId: String, to: CreateTarget? = null): Boolean {
        val query = buildString {
            append("fileId=").append(encodeURIComponent(fileId))
            when (to) {
                null -> Unit
                is CreateTarget.MyDrive -> append("&parentPath=").append(encodeURIComponent(to.path))
                is CreateTarget.SharedFolder -> append("&parentId=").append(encodeURIComponent(to.folderId))
            }
        }
        val response = authedFetch("/api/archive/extract/destination?$query")
        if (!response.ok) {
            capabilityDenied(response, "extract archive")?.let { throw it }
            throw ApiException(response.status.toInt(), "extract archive", response.statusText)
        }
        return apiJson.decodeFromString<ExtractDestinationDto>(response.text().await()).allowed
    }

    /** Enqueue a compress of [fileIds] into `<name>` (format: zip | tar.gz | tar). */
    suspend fun compress(fileIds: List<String>, name: String, format: String): CompressResponse {
        val payload = apiJson.encodeToString(CompressRequest(fileIds, name, format))
        val response = authedFetch("/api/archive/compress", method = "POST", jsonBody = payload)
        if (!response.ok) {
            capabilityDenied(response, "compress files")?.let { throw it }
            val reason = runCatching {
                apiJson.decodeFromString<Map<String, String>>(response.text().await())["error"]
            }.getOrNull()
            throw reason?.let { RuntimeException(it) }
                ?: ApiException(response.status.toInt(), "compress files", response.statusText)
        }
        return apiJson.decodeFromString(response.text().await())
    }

    /**
     * Start an OPC run (ADV-669) — `POST /api/pipeline/curated/run` — and return the
     * minted job's id.
     *
     * The backend's own 400s are named and written for a scientist to read ("curated
     * workflows are not currently enabled", "'outdir' is platform-owned…", "curated input
     * 'reads' picked the folder …"), so the `{"error": …}` body is unwrapped and thrown as
     * its own message rather than being flattened into a status code — the same treatment
     * [pull] gives its named rejections.
     */
    suspend fun runCuratedWorkflow(req: CuratedRunRequest, shared: Boolean = false): String {
        val response = authedFetch(
            "/api/pipeline/curated/run",
            method = "POST",
            jsonBody = apiJson.encodeToString(req),
        )
        if (!response.ok) {
            val status = response.status.toInt()
            val fields = runCatching { bodyFields(response.text().await()) }.getOrNull().orEmpty()
            val code = fields["error"]
            // ADV-1330: [shared] is a run on files in a shared folder. Its refusals read as
            // sentences, and its 404 (a pick no longer shared) is a typed refusal, so the store
            // reads the grants again. A 503 is typed for every run, so it never shows its code.
            val sentence = curatedRunRefusal(status, fields, shared)
            if (status == 503) throw ApiException(503, "start workflow", response.statusText, code)
            if (shared && status == 404) {
                throw ApiException(404, "start workflow", response.statusText, code, detail = sentence, gone = true)
            }
            // A 403 keeps its code, so an own-space refusal still earns the 1326c re-poll.
            if (status == 403 && code == CAPABILITY_DENIED) {
                throw ApiException(403, "start workflow", response.statusText, code, detail = sentence)
            }
            throw (sentence ?: code)?.let { RuntimeException(it) }
                ?: ApiException(status, "start workflow", response.statusText)
        }
        val body = apiJson.decodeFromString<Map<String, String>>(response.text().await())
        return body["jobId"] ?: throw ApiException(response.status.toInt(), "start workflow", "no jobId in response")
    }

    /**
     * The OPC catalog, read from wisp's registry via the backend — WITH the reason it is
     * the length it is.
     *
     * The whole response is returned, not just `workflows`, because an empty list is the
     * answer to three different questions (registry unreachable, curated switched off,
     * nothing registered) and only one of them is a problem worth interrupting someone
     * over. Dropping `status` here was what made an outage look like a quiet day.
     *
     * It deliberately does NOT fall back to a baked-in list — showing a workflow that may
     * not be registered is how a submit gets a 202 and a run_id and then silently never
     * launches.
     */
    suspend fun fetchCuratedCatalog(): CuratedCatalogResponse {
        val response = authedFetch("/api/pipeline/curated/catalog")
        if (!response.ok) {
            throw ApiException(response.status.toInt(), "load workflow catalog", response.statusText)
        }
        return apiJson.decodeFromString<CuratedCatalogResponse>(response.text().await())
    }

    /** ADV-1412: a refusal is [deleteFailure], so `403 delete_needs_all_mine` shows its sentence. */
    suspend fun deleteFile(id: String): DeleteResult {
        val response = authedFetch("/api/files/$id", method = "DELETE")
        return when (val status = response.status.toInt()) {
            204 -> DeleteResult.OK
            202 -> DeleteResult.ACCEPTED
            404 -> DeleteResult.NOT_FOUND
            else -> {
                val fields = if (status == 403) runCatching { bodyFields(response.text().await()) }.getOrNull().orEmpty() else emptyMap()
                throw deleteFailure(status, response.statusText, fields)
            }
        }
    }

    /**
     * `/api/me`. ADV-1330 D7: `sharingAllowed` and `orgShareable` are fields of [UserInfoDto]. A
     * missing `sharingAllowed` decodes as false, so it fails closed.
     */
    suspend fun fetchMe(): UserInfoDto {
        val response = authedFetch("/api/me")
        ensureOk(response, "fetch /api/me")
        return apiJson.decodeFromString(response.text().await())
    }

    /**
     * ADV-1326c (RULING 2026-09-28 (1)): `GET /api/me/admission`, the standing the pod holds for
     * the caller. The server reads memory only, so this call costs no az2 or vault call.
     */
    suspend fun fetchMeAdmission(): MeAdmissionDto {
        val response = authedFetch("/api/me/admission")
        ensureOk(response, "fetch /api/me/admission")
        return apiJson.decodeFromString(response.text().await())
    }

    // ---- ADV-1330: the share routes ------------------------------------------------------
    //
    // Each throws [ShareApiException] with the typed code on a non-2xx answer, so the caller can
    // tell a cap (S8) from an error. None of them logs a body: the picker's answers carry names
    // and emails, and nothing here writes them anywhere.

    /** `GET /api/people?q=`. The server answers nobody under 2 characters, and so does the UI. */
    suspend fun searchPeople(q: String): PickerResponse =
        shareJson(authedFetch("/api/people?q=${encodeURIComponent(q)}"))

    /** `GET /api/people/recent`: the caller's recent recipients, newest first. */
    suspend fun recentPeople(): PickerResponse = shareJson(authedFetch("/api/people/recent"))

    /** `GET /api/shares/given` (CR-6.1): the live grants the caller made, newest first. */
    suspend fun listGivenShares(): GivenSharesResponse = shareJson(authedFetch("/api/shares/given"))

    /** `GET /api/me/access` (CR-6.5): the caller's role, teams, org-admin flag, and console link. */
    suspend fun fetchMeAccess(): MeAccessDto = shareJson(authedFetch("/api/me/access"))

    /** `GET /api/files/{id}/access`: "Who has access", with the levels the caller may give. */
    suspend fun fileAccess(fileId: String): FileAccessResponse =
        shareJson(authedFetch("/api/files/${encodeURIComponent(fileId)}/access"))

    /** `POST /api/files/{id}/shares`. */
    suspend fun createShare(fileId: String, principalKind: String, principalId: String, level: String): ShareDto =
        shareJson(
            authedFetch(
                "/api/files/${encodeURIComponent(fileId)}/shares",
                method = "POST",
                jsonBody = apiJson.encodeToString(CreateShareRequest(principalKind, principalId, level)),
            ),
        )

    /** `PUT /api/shares/{grantId}`. */
    suspend fun setShareLevel(grantId: String, level: String): ShareDto =
        shareJson(
            authedFetch(
                "/api/shares/${encodeURIComponent(grantId)}",
                method = "PUT",
                jsonBody = apiJson.encodeToString(SetShareLevelRequest(level)),
            ),
        )

    /** `DELETE /api/shares/{grantId}`: remove a share, or leave one's own. */
    suspend fun revokeShare(grantId: String) {
        val response = authedFetch("/api/shares/${encodeURIComponent(grantId)}", method = "DELETE")
        if (!response.ok) throw shareFailure(response)
    }

    /**
     * `GET /api/shared`, every page (ADV-1329 D9, ADV-1330 D5): the roots shared with the caller,
     * with the sharer and the level of each. The pages are merged into one answer. A `404` (the
     * checks are off) and a `503` (the vault is down) arrive as [ShareApiException].
     */
    suspend fun listShared(): SharedPageResponse {
        var merged: SharedPageResponse? = null
        var token: String? = null
        var pages = 0
        do {
            val url = "/api/shared?limit=$SHARED_PAGE" + (token?.let { "&pageToken=${encodeURIComponent(it)}" } ?: "")
            val page: SharedPageResponse = shareJson(authedFetch(url))
            merged = mergeSharedPage(merged, page)
            token = page.nextPageToken
            pages++
        } while (token != null && pages < SHARED_MAX_PAGES)
        // The page cap stopped the walk with pages left: the list is incomplete.
        return merged!!.let { if (token != null) it.copy(truncated = true) else it }
    }

    /** `GET /api/files?folderId=` (ADV-1329 D8): one folder that is shared with the caller. */
    suspend fun listFolderById(folderId: String): List<FileDto> {
        val response = authedFetch("/api/files?folderId=${encodeURIComponent(folderId)}")
        if (!response.ok) throw shareFailure(response)
        return apiJson.decodeFromString(response.text().await())
    }

    /**
     * ADV-1330 (the shared-folder create slice, scope 9): every file under a folder shared with
     * the caller, every page of `GET /api/files?folderId=&recursive=true&kind=FILE`. The Run
     * workflow wizard walks a picked shared folder with it, because `?path=` is always the
     * caller's own namespace. Each row keeps its owner's `logicalPath`. [collectSubtree] sets
     * the limits.
     */
    suspend fun listSubtreeById(folderId: String): List<FileDto> = collectSubtree { token ->
        val url = "/api/files?folderId=${encodeURIComponent(folderId)}&recursive=true&kind=FILE" +
            (token?.let { "&pageToken=${encodeURIComponent(it)}" } ?: "")
        shareJson(authedFetch(url))
    }

    private suspend inline fun <reified T> shareJson(response: Response): T {
        if (!response.ok) throw shareFailure(response)
        return apiJson.decodeFromString(response.text().await())
    }

    private suspend fun shareFailure(response: Response): ShareApiException {
        val text = runCatching { response.text().await() }.getOrNull()
        val code = runCatching {
            text?.let { apiJson.parseToJsonElement(it).jsonObject["error"]?.jsonPrimitive?.content }
        }.getOrNull()
        return ShareApiException(
            status = response.status.toInt(),
            code = code,
            retryAfterSeconds = response.headers.get("Retry-After")?.toIntOrNull(),
        )
    }

    private const val SHARED_PAGE = 500
    private const val SHARED_MAX_PAGES = 20

    suspend fun fetchAuthConfig(): AuthConfigDto {
        val response = authedFetch("/api/config")
        ensureOk(response, "fetch /api/config")
        return apiJson.decodeFromString(response.text().await())
    }

    suspend fun download(file: FileDto) {
        val response = authedFetch("/api/files/${file.id}/content")
        ensureOk(response, "download")
        val blob = response.blob().await()
        val objectUrl = js("URL.createObjectURL")(blob) as String
        try {
            val a = kotlinx.browser.document.createElement("a") as org.w3c.dom.HTMLAnchorElement
            a.href = objectUrl
            a.setAttribute("download", file.displayName)
            kotlinx.browser.document.body?.appendChild(a)
            a.click()
            a.remove()
        } finally {
            js("URL.revokeObjectURL")(objectUrl)
        }
    }

    // Upload-related calls suppress the auto-logout-on-401: a brief auth blip
    // shouldn't kill an in-flight upload. The orchestrator's retry layer handles
    // these as transient errors with backoff.

    suspend fun startMultipartUpload(req: UploadStartRequest): UploadStartMultipartResponse {
        val response = authedFetch("/api/uploads/multipart", method = "POST",
            jsonBody = encodeCreateBody(req), suppressAuthRedirect = true)
        capabilityDenied(response, "start multipart upload")?.let { throw it }
        destinationGone(response, "start multipart upload")?.let { throw it }
        ensureOk(response, "start multipart upload")
        return apiJson.decodeFromString(response.text().await())
    }

    suspend fun getPartUrl(sessionId: String, partNumber: Int): UploadPartUrlResponse {
        val response = authedFetch("/api/uploads/$sessionId/parts/$partNumber/url",
            suppressAuthRedirect = true)
        ensureOk(response, "get part url $partNumber")
        return apiJson.decodeFromString(response.text().await())
    }

    suspend fun confirmPart(sessionId: String, partNumber: Int, etag: String, sizeBytes: Long) {
        val payload = apiJson.encodeToString(UploadConfirmPartRequest(etag, sizeBytes))
        val response = authedFetch(
            "/api/uploads/$sessionId/parts/$partNumber/confirm",
            method = "POST", jsonBody = payload, suppressAuthRedirect = true,
        )
        ensureOk(response, "confirm part $partNumber")
    }

    suspend fun completeUpload(sessionId: String): FileDto {
        val response = authedFetch("/api/uploads/$sessionId/complete",
            method = "POST", jsonBody = "{}", suppressAuthRedirect = true)
        ensureOk(response, "complete upload")
        return apiJson.decodeFromString(response.text().await())
    }

    suspend fun abortUpload(sessionId: String) {
        authedFetch("/api/uploads/$sessionId", method = "DELETE", suppressAuthRedirect = true)
    }

    /**
     * Fetch the per-user resume key (base64, 32 bytes) used to encrypt the
     * IndexedDB resume metadata at rest. Propagates a non-OK response (e.g. the
     * backend returns 503 when the HMAC secret is unconfigured) as a thrown
     * error via [ensureOk], so callers can treat "resume unavailable" and fall
     * back to non-resumable uploads.
     */
    suspend fun getResumeKey(): ResumeKeyResponse {
        val response = authedFetch("/api/uploads/resume-key")
        ensureOk(response, "GET /api/uploads/resume-key")
        return apiJson.decodeFromString(response.text().await())
    }

    /**
     * What, if anything, can be drawn for this file (ADV-1200).
     *
     * Answers 200 even when the answer is "nothing" — the outcome and message
     * live in the body, because "too large" and "unsupported type" need different
     * wording and both are normal states rather than errors.
     */
    suspend fun previewDescriptor(fileId: String): PreviewDescriptorDto {
        val response = authedFetch("/api/files/$fileId/preview")
        ensureOk(response, "preview")
        return apiJson.decodeFromString(response.text().await())
    }

    /** The bounded preview prefix. Callers must honour [PreviewFetch.truncated]. */
    suspend fun previewContent(fileId: String): PreviewFetch {
        val response = authedFetch("/api/files/$fileId/preview/content")
        // 409 is policy answering, not a failure. See PreviewNotRenderableException.
        if (response.status.toInt() == 409) {
            throw PreviewNotRenderableException(apiJson.decodeFromString(response.text().await()))
        }
        ensureOk(response, "preview content")
        return PreviewFetch(
            response,
            truncated = response.headers.get("X-Drive-Preview-Truncated") == "true",
            rendition = response.headers.get("X-Drive-Preview-Rendition"),
            payloadContentType = response.headers.get("X-Drive-Preview-Payload-Type"),
        )
    }

    suspend fun getUploadStatus(sessionId: String): UploadStatusResponse {
        val response = authedFetch("/api/uploads/$sessionId", suppressAuthRedirect = true)
        ensureOk(response, "get upload status")
        return apiJson.decodeFromString(response.text().await())
    }

    /**
     * @param suppressAuthRedirect when true, a 401 response is returned to the caller
     *   instead of triggering a logout + redirect. Callers in long-running flows (the
     *   UploadOrchestrator) use this so a brief az2 hiccup or expired-token blip doesn't
     *   bounce the user out mid-upload — they'll see the 401 and can retry/refresh.
     */
    private suspend fun authedFetch(
        url: String,
        method: String = "GET",
        jsonBody: String? = null,
        formBody: FormData? = null,
        suppressAuthRedirect: Boolean = false,
    ): Response {
        val headers = Headers()
        val token = AuthApi.currentToken() ?: AuthApi.goToLogin()
        headers.append("Authorization", "Bearer $token")
        if (jsonBody != null) headers.append("Content-Type", "application/json")
        val init = RequestInit(
            method = method,
            headers = headers,
            body = jsonBody ?: formBody,
        )
        val response = window.fetch(url, init).await()
        if (response.status.toInt() == 401 && !suppressAuthRedirect) {
            AuthApi.clearToken()
            AuthApi.goToLogin()
        }
        // ADV-443: a successful authenticated call proves we are not looping — clear the
        // bounce counter so a later legitimate re-login gets a full budget again. Note a 5xx
        // (e.g. 503 when az2 is unreachable) deliberately falls through untouched: the token
        // is kept and the caller can retry.
        if (response.ok) AuthApi.authSucceeded()
        return response
    }
}

/**
 * ADV-1326c (ruling 6): a creation's non-2xx answer. A `403` whose body says `capability_denied`
 * becomes an [ApiException] with that [ApiException.code], so the store can poll the standing
 * once. Every other answer is null, and the caller keeps its own error. The body is read only
 * on a `403`.
 */
private suspend fun capabilityDenied(response: Response, action: String): ApiException? {
    if (response.status.toInt() != 403) return null
    val code = runCatching {
        apiJson.parseToJsonElement(response.clone().text().await()).jsonObject["error"]?.jsonPrimitive?.content
    }.getOrNull()
    return if (code == CAPABILITY_DENIED) ApiException(403, action, response.statusText, code) else null
}

/**
 * ADV-1330 (fresh-eyes M-4): a refused `POST /api/pull`, typed as `runCuratedWorkflow`'s are. A
 * `503` is an [ApiException], so the toast never shows a raw `sharing_unavailable`. A `403` and
 * a `404` are too (the `404` [ApiException.gone]), so a shared refusal reads the grants again.
 * Every other answer keeps the backend's own message (SSRF, over-limit, bad keys are already
 * human), else the mapped status.
 */
internal fun pullFailure(status: Int, statusText: String, fields: Map<String, String>): Throwable {
    val code = fields["error"]
    return when (status) {
        503, 403 -> ApiException(status, "pull", statusText, code)
        404 -> ApiException(404, "pull", statusText, code, gone = true)
        else -> code?.let { RuntimeException(it) } ?: ApiException(status, "pull", statusText)
    }
}

/** ADV-1412: the sentence for `403 delete_needs_all_mine`. */
const val DELETE_NEEDS_ALL_MINE_SENTENCE = "This folder holds files that other people added."

private const val DELETE_NEEDS_ALL_MINE = "delete_needs_all_mine"

/**
 * ADV-1412: a refused `DELETE /api/files/{id}`. Always an [ApiException] with the body's code, so a
 * `403` takes the shared refusal path ([isRefusal]). `403 delete_needs_all_mine` carries
 * [DELETE_NEEDS_ALL_MINE_SENTENCE]: the caller may delete a folder they added only while every row
 * in it is theirs too. Example: Lena's folder Lena's QC in Kai's Run 43 holds Priya's
 * priya.fastq, so her delete says "Delete failed. This folder holds files that other people added."
 */
internal fun deleteFailure(status: Int, statusText: String, fields: Map<String, String>): ApiException {
    val code = fields["error"]
    val detail = if (status == 403 && code == DELETE_NEEDS_ALL_MINE) DELETE_NEEDS_ALL_MINE_SENTENCE else null
    return ApiException(status, "delete file", statusText, code, detail = detail)
}

/** A create's `404`: its destination is gone for the caller ([ApiException.gone]). Else null. */
private fun destinationGone(response: Response, action: String): ApiException? =
    if (response.status.toInt() == 404) ApiException(404, action, response.statusText, gone = true) else null

/** Shown when a shared folder has more pages of files than [collectSubtree] reads. */
internal const val SUBTREE_TOO_LARGE = "This folder has too many files to pick at once."

/**
 * ADV-1330 (review): the pages of a recursive listing, from [fetchPage] (null for the first
 * page). It stops at the last page, or when a `nextPageToken` repeats (a server that loops would
 * otherwise never end). With pages left after [maxPages], it throws [SUBTREE_TOO_LARGE] rather
 * than hand the wizard part of a folder as if it were all of it.
 */
internal suspend fun collectSubtree(
    maxPages: Int = SUBTREE_MAX_PAGES,
    fetchPage: suspend (token: String?) -> FileTreeResponse,
): List<FileDto> {
    val rows = mutableListOf<FileDto>()
    val seen = mutableSetOf<String>()
    var token: String? = null
    var pages = 0
    do {
        if (pages == maxPages) throw RuntimeException(SUBTREE_TOO_LARGE)
        val page = fetchPage(token)
        pages++
        rows += page.items
        token = page.nextPageToken
        if (token != null && !seen.add(token)) break
    } while (token != null)
    return rows
}

/** 20 pages of the server's default 500 rows, as [FilesApi.listShared] caps its walk. */
private const val SUBTREE_MAX_PAGES = 20

/** The string fields of a JSON error body. A field that is not a string is left out. */
private fun bodyFields(text: String): Map<String, String> =
    apiJson.parseToJsonElement(text).jsonObject.mapNotNull { (k, v) ->
        (v as? JsonPrimitive)?.takeIf { it.isString }?.content?.let { k to it }
    }.toMap()

/**
 * ADV-1330 (the shared-folder create slice, scope 9): the sentence for a refused curated run
 * (`POST /api/pipeline/curated/run`), or null to keep the answer's own message.
 *
 * - `400 picks_mixed_owner`: the picks belong to two owners, so the run has no single home.
 *
 * For a [shared] run only (My Drive keeps its old messages and the 1326c behaviour):
 * - `403 capability_denied` with `destination`: no `upload` on the anchor's folder, where the
 *   outputs go. At an owner's root (`/`) a sharee can never write.
 * - `403 capability_denied` with `input`: no `execute` on a pick of that input.
 * - `404`: a pick the caller can no longer read, so its grant went meanwhile.
 *
 * Example: Lena picks a samplesheet from Run 42 and FASTQs from her own drive, and she reads
 * "Pick files that belong to one person. A workflow cannot run on files from two owners."
 */
internal fun curatedRunRefusal(status: Int, fields: Map<String, String>, shared: Boolean): String? {
    val code = fields["error"]
    if (code == PICKS_MIXED_OWNER) {
        return "Pick files that belong to one person. A workflow cannot run on files from two owners."
    }
    if (!shared) return null
    if (status == 404) return "A picked file is no longer shared with you."
    if (status != 403 || code != CAPABILITY_DENIED) return null
    val destination = fields["destination"]
    val input = fields["input"]
    return when {
        destination == "/" ->
            "The results cannot go into the top level of another person's drive. Pick a main input file inside a shared folder."
        destination != null ->
            "You cannot add files to the folder that holds the main input file, so the run has nowhere to put its results."
        input != null -> "You can view the files picked for \"$input\" but not run workflows on them."
        else -> null
    }
}

private const val PICKS_MIXED_OWNER = "picks_mixed_owner"

private fun ensureOk(response: Response, action: String) {
    if (!response.ok) {
        // ADV-443: typed so callers can render ApiException.userMessage instead of a raw status.
        throw ApiException(response.status.toInt(), action, response.statusText)
    }
}

private fun encodeURIComponent(value: String): String = js("encodeURIComponent(value)") as String
